// REACT IMPORTS
import React, { useState, useEffect } from "react";

// CSS
import "./AccessLog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall } from "../../../config/apiCalls";

// MUI COMPONENTS
import { CircularProgress } from "@mui/material";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import TanStackTable from "../../Misc/Table/TanStackTable";

// CONSTANTS
import { convertToPST } from "../../../constants/dateTime";
import {
	dateSort,
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";

// REACT COMPONENT
const AccessLog = (props) => {
	// When embedded inside another view (e.g. the Access Logs Dashboard's
	// "Full List" toggle), the host page already provides its own
	// title/padding, and drives refreshes through refreshTrigger.
	const { embedded, refreshTrigger } = props;

	// MSAL
	const { instance, accounts } = useMsal();

	// LOGS STATE
	const [accessLogs, setAccessLogs] = useState([]);

	const [accessLogsColumns, setAccessLogsColumns] = useState([]);
	const [accessLogsRows, setAccessLogsRows] = useState([]);

	// LOADING STATES
	const [isLoading, setIsLoading] = useState(false);

	// USE EFFECT
	useEffect(() => {
		fetchAccessLogs();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTrigger]);

	useEffect(() => {
		createColumnData();
		createRowData();
	}, [accessLogs]);

	// INPUT HANDLERS

	// API HELPER FUNCTIONS
	const fetchAccessLogs = async () => {
		setIsLoading(true);

		try {
			const response = await getAPICall(
				instance,
				accounts[0],
				"/api/logs/access/all"
			);
			setAccessLogs(response.data);
		} catch (error) {
			console.error(error);
		}

		setIsLoading(false);
	};

	const createColumnData = () => {
		setAccessLogsColumns([
			{
				accessorKey: "employee_name",
				header: () => <span>Employee</span>,
				label: "Employee",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "created",
				header: () => <span>Time</span>,
				label: "Time",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "page_name",
				header: () => <span>Tool</span>,
				label: "Tool",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "notes",
				header: () => <span>Message</span>,
				label: "Message",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
		]);
	};

	const createRowData = () => {
		setAccessLogsRows(
			accessLogs.map((log) => ({
				employee_name: log.employee_name,
				created: convertToPST(log.created),
				page_name: log.page_name,
				notes: log.notes,
			}))
		);
	};

	// RENDER
	return (
		<div className={embedded ? "AccessLog embedded" : "AccessLog"}>
			{!embedded && (
				<div className="accessLogHeader">
					<div className="accessLogHeaderTitle">Access Logs</div>
				</div>
			)}
			{isLoading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!isLoading && (
				<div className="tableContainerDiv">
					{accessLogs.length === 0 && <J2NoResultsFound />}
					{accessLogs.length > 0 &&
						accessLogsColumns.length > 0 &&
						accessLogsRows.length > 0 && (
							<TanStackTable
								columnData={accessLogsColumns}
								rowData={accessLogsRows}
							/>
						)}
				</div>
			)}
		</div>
	);
};

export default AccessLog;
